import React, { forwardRef, useState } from 'react';

import AutocompleteInput from '~/src/common/components/Form/AutocompleteInput/AutocompleteInput';
import mapGeocoderResultToDeliveryAddress from '~/src/common/components/GoogleAutocompleteInput/utils/mapGeocoderResultToDeliveryAddress';
import Loader from '~/src/common/components/Loader';
import { useGoogleMapsScriptTag } from '~/src/common/hooks/useGoogleMapsScriptTag';
import { AutocompleteResult } from '~/src/common/services/Google/types';
import I18n from '~/src/common/services/I18n';
import Tracker from '~/src/common/services/Tracker';
import { DeliveryAddress } from '~/src/common/typings/cart';

import useGeolocation from './hooks/useGeolocation';
import useSearchAddressPlaces from './hooks/useSearchAddressPlaces';
import useSearchAnalytics from './hooks/useSearchAnalytics';
import useSessionToken from './hooks/useSessionToken';
import { GeolocIcon, GeolocIconWrapper, Item, LoaderWrapper, SearchIcon } from './layout';
import highlightMatch from './utils/highlightMatch';
import mapPlaceToDeliveryAddress from './utils/mapPlaceToDeliveryAddress';

type Props = Pick<
  React.ComponentProps<typeof AutocompleteInput>,
  'error' | 'ResultFooter' | 'onClear'
> &
  Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> & {
    isSubmitting?: boolean;
    onSelectResult: (address: DeliveryAddress) => Promise<void> | void;
    onChange?: (value: string) => void;
    defaultValue?: string;
  };

const GoogleAddressAutocompleteInput = forwardRef<HTMLInputElement, Props>(
  (
    {
      defaultValue = '',
      disabled,
      isSubmitting = false,
      onSelectResult,
      ResultFooter,
      onChange,
      onClear,
      onFocus,
      ...inputProps
    }: Props,
    ref,
  ) => {
    const [value, setValue] = useState(defaultValue);
    const [error, setError] = useState('');
    const [isSearching, setSearching] = useState(false);

    const { getSessionToken, generateNewSessionToken } = useSessionToken();

    const { handleSendEvent } = useSearchAnalytics();

    const { isGoogleMapsLoaded } = useGoogleMapsScriptTag();

    const handleGeolocationSuccess = (results: google.maps.GeocoderResult[]) => {
      setValue(results?.[0]?.formatted_address);

      Tracker.sendEvent('shipping type submitted', {
        'shipping type': 'delivery',
        'shipping type step': 'enter the address',
        'event source': 'navigation',
      });

      const result = results[0];

      // La présence du numéro est contrôlée avant d'appeler l'API
      // mais aussi après pour éviter d'afficher des résultats qui ne seraient pas pertinents
      if (result.address_components.some(({ types }) => types.includes('street_number'))) {
        const address = mapGeocoderResultToDeliveryAddress(result);
        void onSelectResult(address);
      } else {
        setError(I18n.t('google-autocomplete.street-number-missing'));
      }
    };

    const { handleGeolocation, isLoading: isGeolocating } = useGeolocation({
      onSuccess: handleGeolocationSuccess,
    });

    const { places, searchPlaces, clearPlaces } = useSearchAddressPlaces({
      setError,
      setSearching,
    });

    const handleChangeValue = (v = '') => {
      handleSendEvent();

      onChange?.(v);

      setValue(v);

      if (!v) {
        clearPlaces();

        setError('');
      } else {
        setSearching(true);

        void searchPlaces(v, getSessionToken());
      }
    };

    const handleSelectItem = (selectedItem: AutocompleteResult) => {
      Tracker.sendEvent('shipping type submitted', {
        'shipping type': 'delivery',
        'shipping type step': 'enter the address',
        'event source': 'navigation',
      });

      generateNewSessionToken();

      if (
        selectedItem.place.addressComponents?.some(({ types }) => types.includes('street_number'))
      ) {
        const address = mapPlaceToDeliveryAddress(selectedItem.place);
        void onSelectResult(address);
      } else {
        setError(I18n.t('google-autocomplete.street-number-missing'));
      }
    };

    return (
      <AutocompleteInput
        {...inputProps}
        ref={ref}
        loading={isSearching}
        disabled={disabled ?? !isGoogleMapsLoaded}
        items={places}
        onChange={handleChangeValue}
        error={error}
        value={value}
        onSelectItem={handleSelectItem}
        onClear={() => {
          setValue('');
          onClear?.();
        }}
        itemToString={item => item?.place?.formattedAddress ?? ''}
        renderItem={(item, isHighlighted) => (
          <Item
            key={item.place.id}
            aria-selected={isHighlighted}
            dangerouslySetInnerHTML={{
              __html: highlightMatch(item.place?.formattedAddress ?? '', item.matches),
            }}
          />
        )}
        ResultFooter={ResultFooter}
        LeftIcon={<SearchIcon name="marker-normal" size={24} />}
        RightIcon={
          isSubmitting || isGeolocating ? (
            <LoaderWrapper>
              <Loader />
            </LoaderWrapper>
          ) : (
            <GeolocIconWrapper onClick={() => void handleGeolocation()}>
              <GeolocIcon name="geolocation" size={16} />
            </GeolocIconWrapper>
          )
        }
      />
    );
  },
);

export default GoogleAddressAutocompleteInput;
